/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * The transfer page's "Upcoming cut-over" card: the transfer's active cut-over request (only one
 * can be active), its window, where it stands and who has decided.
 */

import * as React from "react";
import styled from "styled-components";
import { ArrowRight, CalendarClock } from "lucide-react";

import { ActionButton, KeyValueGrid, SectionCard } from "@src/components/ui/DetailsKit";
import { Pill, T9 } from "@src/components/ui/Lists/ListTable";
import { Notice } from "@src/components/ui/OpsDialog";

import type { CutoverRequest } from "@src/@types/Cutover";

import {
  formatDuration,
  formatUtc,
  formatWindow,
  instancesLabel,
  relative,
  requesterName,
  statusInfo,
  windowPhase,
} from "./cutoverUtils";
import { parseApiDate } from "@src/components/ui/Lists/ListTable";

const Countdown = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-bottom: 14px;
  .big {
    font-size: 20px;
    line-height: 28px;
    font-weight: 600;
    color: ${T9.slate900};
    font-variant-numeric: tabular-nums;
  }
  .small {
    font-size: 13px;
    color: ${T9.slate500};
  }
`;

type Props = {
  request: CutoverRequest;
  transferInstances?: string[];
  onOpen: () => void;
  now?: number;
};

const phaseLine = (r: CutoverRequest, now: number) => {
  const phase = windowPhase(r, now);
  if (r.status === "started") return { big: "Deploying now", small: `started ${relative(r.started_at, now)}` };
  if (phase === "before") return { big: relative(r.window_start, now).replace(/^in /, "In "), small: `the window opens ${formatWindow(r.window_start, r.window_end)}` };
  if (phase === "open") return { big: "Window open", small: `closes ${relative(r.window_end, now)}` };
  return { big: "Window closed", small: formatWindow(r.window_start, r.window_end) };
};

const UpcomingCutoverCard = ({ request: r, transferInstances, onOpen, now = Date.now() }: Props) => {
  const info = statusInfo(r.status);
  const line = phaseLine(r, now);
  const approvals = (r.approvals || []).filter(a => a.decision === "approved");
  const start = parseApiDate(r.window_start);
  const end = parseApiDate(r.window_end);
  return (
    <SectionCard
      title="Upcoming cut-over"
      icon={CalendarClock}
      subtitle="The change request that deploys this transfer"
      actions={
        <ActionButton type="button" variant="secondary" onClick={onOpen} data-test-id="upcomingCutover-open">
          Open request
          <ArrowRight size={16} />
        </ActionButton>
      }
      testId="upcomingCutoverCard"
    >
      <Countdown>
        <span className="big">{line.big}</span>
        <span className="small">{line.small}</span>
        <span style={{ marginLeft: "auto" }}>
          <Pill tone={info.tone} icon={info.icon} spin={info.spin}>
            {info.label}
          </Pill>
        </span>
      </Countdown>
      <KeyValueGrid
        columns={4}
        items={[
          { label: "Change window", value: formatWindow(r.window_start, r.window_end), span: 2 },
          { label: "In UTC", value: `${formatUtc(r.window_start)}${start && end ? ` · ${formatDuration(end.getTime() - start.getTime())}` : ""}`, span: 2 },
          { label: "VMs", value: instancesLabel(r, transferInstances) },
          { label: "Requested by", value: `${requesterName(r)} · ${relative(r.created_at, now)}` },
          {
            label: "Approved by",
            value: approvals.length ? approvals.map(a => a.user_name || a.user_id.slice(0, 8)).join(", ") : null,
          },
          { label: "Reason", value: r.reason || null },
        ]}
      />
      {r.status === "pending_approval" ? (
        <Notice tone="warning" style={{ marginTop: 14 }}>
          Waiting for approval. SwiftIQ deploys only after someone other than {requesterName(r)} approves it on the Change
          control page.
        </Notice>
      ) : null}
    </SectionCard>
  );
};

export default UpcomingCutoverCard;
